/* eslint-disable */
import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Api } from 'services/api';
import { updateUrlForEU } from 'services/api/config';
import { addCss, AppMessenger, ariConfig, getLanguageCode } from 'utils';
import { outerCSSString } from 'styles/outerStyles';
import { cssString } from 'styles/config';
import { addDefaults, addHighlightingCapability } from 'styles/styleUtils';
import { setDefaultAriText, toggleWidget, setIsAnon, setIsChatWidgetOpen } from 'redux/actions';
import { activateChatDemoMode, setButtonDemoMode, shouldButtonsBeDemoed } from 'chatDemoMode';
import { cacheKey } from 'appConstants';
import { routes } from 'routes';
import { cache } from 'utils/cache';
import {
  WIDGET_READY,
  SET_JOB_CHAT_ID,
  UPDATE_CONFIG,
  OPEN_WIDGET,
  APP_READY,
  RENDER_WIDGET,
} from 'widgetActions';

const minInMs = 1e3 * 60;
const twoHrsInMins = 120;

export function useWidget() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const [openWidget, setOpenWidget] = React.useState(false);
  const [isWidgetReady, setIsWidgetReady] = React.useState(false);

  const defaultText = t('ready_to_chat_click_here', 'Ready to chat? Click here');
  const widgetButtonTxt = t('chat_widget_button', 'Chat Widget Button');

  /**
   * Add message event-listener for reactions to widget messages.
   * Inform widget React-app is ready
   */
  React.useEffect(() => {
    window.addEventListener('message', eventMessageHandler);
    AppMessenger.sendMessageToWidget({ action: APP_READY });
    return () => window.removeEventListener('message', eventMessageHandler);
  }, []);

  React.useEffect(() => {
    if (isWidgetReady && openWidget) {
      AppMessenger.sendMessageToWidget({ action: OPEN_WIDGET });
      dispatch(toggleWidget());
    }
  }, [isWidgetReady, openWidget]);

  function renderJobChatWidget(styles, config) {
    AppMessenger.sendMessageToWidget({
      action: RENDER_WIDGET,
      additionalData: { styles, config },
    });
  }

  function eventMessageHandler({ data }) {
    if (data) {
      const { action, branding, jobChatId, cloudEnvironment } = data;

      if (action === WIDGET_READY) {
        setIsWidgetReady(() => true);
      }
      if (action === SET_JOB_CHAT_ID) {
        cache.set(cacheKey.JOB_CHAT_ID, jobChatId);

        // For EU accounts, update base API URL & RealTime URL before making any server requests
        updateUrlForEU(cloudEnvironment);
        getCustomization(jobChatId);
      }
      if (action === OPEN_WIDGET) {
        dispatch(toggleWidget());
        // Temp Solution: setting state `IsChatWidgetOpen` in appReducer as true when widget is open to show unread notification.
        dispatch(setIsChatWidgetOpen());

        /**
         * Remove keep-widget-closed after user has re-opened the widget.
         * This will re-enable proactive ARI to automatically open the widget
         * and engage with the user.
         */
        localStorage.removeItem(cacheKey.KEEP_WIDGET_CLOSED);
      }
      if (action === UPDATE_CONFIG) {
        if (data.chatDemoMessages) {
          setButtonDemoMode(shouldButtonsBeDemoed(branding));
          activateChatDemoMode(data.chatDemoMessages);
          history.replace(routes.THREAD);
        }

        const branding2 = addDefaults(branding);
        addCss(addHighlightingCapability(branding2, cssString));
        renderJobChatWidget(addHighlightingCapability(branding2, outerCSSString), {
          ...branding2,
          defaultText,
          widgetButtonTxt,
        });
      }
    }
  }

  function getCustomization(jobChatId) {
    Promise.all([Api.getV2Styles(jobChatId), Api.getConfig(jobChatId)])
      .then((values) => {
        const [styleResponse, configResponse] = values;

        if (configResponse.ariDisplayName) {
          ariConfig.displayName = configResponse.ariDisplayName;
        }

        if (styleResponse.branding) {
          ariConfig.imgUrl = styleResponse.branding.jobChatAvatar || '';
          ariConfig.imgAlt = styleResponse.branding.jobChatAvatarDescription || '';
        }

        // Temp Solution for language change wrt to admin settings and can be refactored. -Rajat
        i18n.changeLanguage(getLanguageCode(configResponse.language));

        const allStyles = addDefaults({
          ...styleResponse.branding,
          ...configResponse,
          defaultText,
          widgetButtonTxt,
        });

        addCss(cssString(allStyles));
        renderJobChatWidget(outerCSSString(allStyles), allStyles);
        dispatch(setDefaultAriText(configResponse.defaultIntroChatText));

        // Set job chat mode to anon
        if (configResponse.isAnonJobChat) {
          dispatch(setIsAnon());
        }

        /**
         * For ari-initiates configuration:
         * 1. Check localStorage for the timestamp of last time user closed/ended the chat
         * 2. If user had closed/ended the chat within the past 2-hours, do not
         * 		re-open the widget.
         * See: https://jira.icims.net/browse/TXTE-17463
         */
        const keepWidgetClosed = localStorage.getItem(cacheKey.KEEP_WIDGET_CLOSED);
        if (configResponse.ariInitiates) {
          let openWidget = true;
          if (keepWidgetClosed) {
            const timeInMs = parseInt(keepWidgetClosed);
            const diffInMins = Math.floor((Date.now() - timeInMs) / minInMs);
            if (diffInMins < twoHrsInMins) {
              openWidget = false;
            }
          }
          setOpenWidget(openWidget);
        }

        /**
         * If an existing valid token is found:
         * 1. Set job chat mode: anon or non-anon
         * 2. Redirect to live chat session
         */
        if (cache.get(cacheKey.TOKEN)) {
          setOpenWidget(true);
          return history.replace(routes.THREAD);
        }

        if (configResponse.isAnonJobChat) {
          return history.replace(routes.SPOOFED_THREAD);
        }
      })
      .catch((_error) => {
        // TODO: Should set default styles/config after retries; also send error msg tto Sumo
        addCss(cssString({}));
        renderJobChatWidget({});
      });
  }
}
